import { dispatchTrackToTags } from '@core/tags/index.js';

import { EVENTS, type EventName, type EventProps } from './events.js';

/**
 * Emit a typed analytics event. The event name and the property shape are
 * constrained by the EVENTS registry — renaming an event or changing its
 * schema produces a TypeScript error at every call site.
 *
 * `track()` is pure dispatch: validate against the registry, then fan the
 * validated payload out to the tag container. Each registered tag decides
 * what to do with the event (PostHog queues until consent + init; Sentry
 * drops a breadcrumb unconditionally; future tags follow their own ready
 * semantics). Never throws — invalid props log + early-return so analytics
 * errors cannot break user-facing flows.
 *
 * **PII discipline.** Every successful track() reaches every active tag with
 * the same validated payload. Sentry's breadcrumb buffer is in-memory and
 * only leaves the device when attached to a sent error event; Sentry errors
 * are classified `necessary` per consent-management/design.md (cookiewet
 * strictly-necessary carve-out, `sendDefaultPii: false`, no replay). PostHog
 * is consent-gated end-to-end. Registry schemas must never accept PII (no
 * emails, names, free-form user input, raw search queries, etc.). The
 * current event set uses only opaque UUIDs + enums + numeric counts. When
 * adding a new event, audit its schema for PII before merging.
 */
export function track<E extends EventName>(event: E, props: EventProps<E>): void {
  const def = EVENTS[event];
  const result = def.schema.safeParse(props);
  if (!result.success) {
    if (import.meta.env.DEV) {
      console.error(`[track] Invalid props for "${event}":`, result.error.format());
    } else {
      console.warn(`[track] Invalid props for "${event}"`);
    }
    return;
  }
  dispatchTrackToTags(event, result.data as Record<string, unknown>);
}
